import {
  all,
  call,
  debounce,
  fork,
  getContext,
  put,
  race,
  select,
  take,
  takeEvery,
} from 'redux-saga/effects';
import { damzOffline, damzOnline } from '../actions/initialization';
import { SupabaseClient } from '@supabase/supabase-js';
import { buffers, eventChannel } from 'redux-saga';
import { PapiDamz } from '@wix/da-papi-types';
import ddt from '@wix/da-ddt';
import {
  addActivityQueue,
  clearActivityQueue,
  onActivity,
  playNotificationSound,
  sendAlertAndUpdateDrawer,
} from '../actions/global';
import { updatedToken } from '../actions/realtime';
import { refreshCounter } from '../actions/counter';
import { getActivityRoomQueue } from '../selectors/global';
import { activityUpdateFrequencyMs } from '../../constants';
import { Database } from '@wix/da-papi-types/lib/supabase';
import uniq from 'lodash/uniq';
import { roomFetchToRoomItem, getDrawerStreamId } from '../helpers';
import { getDrawerActiveFilter } from '../selectors/drawer';
import { RoomFlags, RoomItem } from '../../types';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import i18next from 'i18next';
import { toggleWindowTabAsUnread } from '../helpers/window';
import {
  withCursor,
  selectors as streamSelectors,
} from '@wix/da-shared-react/pkg/Stream';
import { saveGoodState } from '../actions/storage';
import { getCanConnectToDamz } from '../selectors/storage';
import { moveItemsToStreamHead } from './helpers';
import { realtimeConnections } from '../RealtimeConnections';

function* listenToGlobalChannel() {
  const client: SupabaseClient = yield getContext('supaClient');
  const damzConfig: PapiDamz = yield getContext('damzConfig');

  if (!damzConfig.userChannel) {
    if (Math.random() < 0.01) {
      ddt.alert(
        'damz',
        'Missing supabase config - userChannel',
        ['damz', 'fatal'],
        damzConfig
      );
    }
    return;
  }

  try {
    realtimeConnections.setClient(client);
    const bindEmit = realtimeConnections.startUserChannel(
      damzConfig.userChannel
    );

    const sagaChannel = eventChannel(
      emitter => bindEmit(emitter),
      buffers.expanding<any>()
    );

    while (true) {
      try {
        const payload = yield race({
          chan: take(sagaChannel),
          offline: take(damzOffline),
        });

        if (payload.offline) {
          sagaChannel.close();
          break;
        }

        if (payload.chan) {
          ddt.log('damz', 'Realtime event received', payload.chan);
          if (payload.chan.event === 'activity') {
            if (payload.chan.payload.room_id) {
              yield put(onActivity(payload.chan.payload.room_id));
            }
            if (payload.chan.payload.token) {
              yield put(
                updatedToken(
                  payload.chan.payload.token.token,
                  payload.chan.payload.token.expires_at,
                  payload.chan.payload.token.id
                )
              );
            }
          }
        }
      } catch (err) {
        ddt.error('damz', 'Realtime processing error', err);
        sagaChannel.close();
      }
    }
  } catch (err) {
    ddt.error('damz', 'Error creating saga channel', err);
    yield put(
      addErrorNotification(i18next.t('damz:damz.realtime.global.error'))
    );
  }
}

function* handleActivity(action: ReturnType<typeof onActivity>) {
  // Queue rooms updates
  yield put(addActivityQueue(action.payload.room_id));

  // throttled call to get global counter
  yield put(refreshCounter());

  // debounced call to alerts and drawer updates
  yield put(sendAlertAndUpdateDrawer());
}

function* handleActivityAlerts() {
  const room_ids = yield select(getActivityRoomQueue);

  if (room_ids.length === 0) {
    return;
  }

  // Fetch the updated rooms
  const client: SupabaseClient<Database, 'damz'> =
    yield getContext('supaClient');

  const response = yield client.rpc('get_conversations_by_room_id', {
    p_room_ids: uniq(room_ids),
  });

  // The logic here is:
  // 1. Fetch the updated rooms
  // 2. Filter the rooms to only include the ones that match the active filter (enables removal of flags)
  // 3. Get the current items in the stream for comparison
  // 4. Compare the current items with the new items
  // 5. If the item has updated (has new msg), move it to the head of the stream
  // 6. If the item has not updated, replace it in the stream
  if (response && response.data && response.data.length > 0) {
    const newItems: RoomItem[] = roomFetchToRoomItem(response.data);
    const filter = yield select(getDrawerActiveFilter);
    const streamId = getDrawerStreamId(filter);

    // Only keep items where the flag is set, unless filter is 'all'
    // This stops us adding say starred room back to the starred room when you remove the star
    const filteredItems: RoomItem[] =
      filter === 'all'
        ? newItems.filter(
            item =>
              !(item.roomMeta.flags & RoomFlags.request) &&
              !(item.roomMeta.flags & RoomFlags.archived)
          )
        : newItems.filter(
            item => (item.roomMeta.flags & RoomFlags[filter]) !== 0
          );

    if (filteredItems.length === 0) {
      yield put(clearActivityQueue());
      return;
    }

    // Get current items in the stream for comparison
    const currentItems: RoomItem[] = yield select(
      streamSelectors.getItems,
      streamId
    );

    // Split into flag-only changes and others
    const flagOnlyItems: { oldItem: RoomItem; newItem: RoomItem }[] = [];
    const moveToHeadItems: RoomItem[] = [];
    for (const newItem of filteredItems) {
      const oldItem = currentItems.find(
        i => i.room.room_id === newItem.room.room_id
      );

      if (oldItem) {
        // Has it updated?
        const updatedAtChanged =
          oldItem.room.updated_at !== newItem.room.updated_at;
        const msgChanged = oldItem.lastMsg?.msg !== newItem.lastMsg?.msg;

        if (!updatedAtChanged && !msgChanged) {
          flagOnlyItems.push({ oldItem, newItem });
        } else {
          moveToHeadItems.push(newItem);
        }
      } else {
        // New item, treat as move to head
        moveToHeadItems.push(newItem);
      }
    }

    // Replace flag-only items at same offset
    for (const { oldItem, newItem } of flagOnlyItems) {
      yield put(
        withCursor.actions.replaceItem({
          streamId,
          newItem,
          predicate: (item: RoomItem) =>
            item.room.room_id === oldItem.room.room_id,
        })
      );
    }

    // Remove and re-insert only the items that need to move to head
    if (moveToHeadItems.length > 0) {
      yield call(moveItemsToStreamHead, streamId, moveToHeadItems);
    }

    // Update tab title and favicon if tab is hidden and there is a new message
    const hasUnmutedItems = moveToHeadItems.some(
      item => !(item.roomMeta.flags & RoomFlags.muted)
    );

    if (hasUnmutedItems && document.visibilityState === 'hidden') {
      const firstMatchingItem = moveToHeadItems.find(
        item =>
          item.lastMsg?.user?.username &&
          item.roomMeta.flags & RoomFlags.unread &&
          !(item.roomMeta.flags & RoomFlags.muted)
      );
      const lastMsgUsername = firstMatchingItem?.lastMsg?.user?.username || '';

      const environmentType = yield select(state => state.environment.type);
      toggleWindowTabAsUnread(environmentType, lastMsgUsername);
      yield put(playNotificationSound(new Date()));
    }
  }

  yield put(clearActivityQueue());
}

function* canConnectToDamz() {
  const client: SupabaseClient<Database, 'damz'> =
    yield getContext('supaClient');

  // Only check every 30 days
  const canConnectToDamzState = yield select(getCanConnectToDamz);
  if (canConnectToDamzState) {
    const diff = Date.now() - canConnectToDamzState;
    if (diff < 1000 * 60 * 60 * 24 * 30) {
      return;
    }
  }

  const { data, error } = yield client.rpc('can_use_chat');

  if (error || data === false) {
    ddt.error('damz', 'User missing data', error);
    yield put(damzOffline(error ?? 'User missing data'));
    return;
  }

  yield put(saveGoodState());
}

/**
 * Listens to realtime events this user might be interested in
 */
export function* globalSaga() {
  yield take(damzOnline); // wait for online to happen
  yield call(canConnectToDamz);
  yield all([
    fork(listenToGlobalChannel),
    takeEvery(onActivity, handleActivity),
    debounce(
      activityUpdateFrequencyMs,
      sendAlertAndUpdateDrawer,
      handleActivityAlerts
    ),
  ]);
}
